import getFrontConfig from '../getFrontConfig';

/**
 * Return color from pid
 *
 * Examples:
 * 201BR48P3.T1000M => T1000
 * 201BR48P3.T1000_DLM => T1000
 * 201BR48P3.T1000 => T1000
 * 201BR48P3 => null
 *
 * @param {string} pid ID of a variant or variation group
 * @return {string|null} Color code
 */
const getColorCodeFromPid = (pid) => {
  let result = null;
  if (!pid) return result;

  const matches = pid.match(/\.([a-zA-Z][0-9]{4}|\d{1}\w{1}\d{3})/);
  const match = matches && matches.length > 0 && matches[0];
  if (match) {
    // Return match (ex: '.T1000') without initial dot
    result = match.substring(1);
  }
  return result;
};

/**
 * Return pid without color and size variation
 *
 * Examples:
 * 201BR48P3.T1000M => 201BR48P3
 * 201BR48P3.T1000 => 201BR48P3
 * 201BR48P3 => 201BR48P3
 *
 * @param {string} pid
 * @return {string}
 */
const getMasterProductId = (pid) => {
  if (pid && pid.indexOf('.') > 0) {
    return pid.substring(0, pid.indexOf('.'));
  }
  return pid;
};

/**
 * Return pid without size variation
 *
 * Examples:
 * 201BR48P3.T1000M => 201BR48P3.T1000
 * 201BR48P3.T1000 => 201BR48P3.T1000
 * 201BR48P3 => 201BR48P3
 *
 * @param {string} pid
 * @return {string}
 */
const getPidWithoutSize = (pid) => {
  if (!pid) return pid;
  const matches = pid.match(/.+\.([a-zA-Z][0-9]{4}|\d{1}\w{1}\d{3})/);
  const match = matches?.length > 0 && matches[0];
  if (match) {
    return match;
  }
  return pid;
};

/**
 * Return pid with size
 *
 * Examples:
 * 201BR48P3.T1000 => 201BR48P3.T1000M
 * 201BR48P3.T1000 => 201BR48P3.T1000UN
 * 201BR48P3 => 201BR48P3
 *
 * @param {string} variationGroupId
 * @param {string} size
 * @return {string}
 */
const addSizeToVariationGroupId = (variationGroupId, size) => {
  if (!variationGroupId || !size) return variationGroupId;
  const matches = variationGroupId.match(/.+\.([a-zA-Z][0-9]{4}|\d{1}\w{1}\d{3})/);
  const match = matches?.length > 0 && matches[0];
  if (match) {
    return match + size;
  }
  return variationGroupId;
};

/**
 * Check whether a product ID is a master product
 *
 * @param {string} pid Product ID
 * @return {boolean}  true or false
 */
const isMasterProductId = (pid) => !!pid.match(/^[^.]+$/);

/**
 * Check whether a product ID is a variation group
 *
 * @param {string} pid Product ID
 * @return {boolean}  true or false
 */
const isVariationGroupId = (pid) => !!pid.match(/^[^.]+\.(?:[a-zA-Z][0-9]{4}|\d{1}\w{1}\d{3})$/);

/**
 * It checks if a product is product variation (pid with size)
 *
 * @param {Object|string} product one product or its pid
 * @return {boolean}  true or false
 */
const isVariationProductId = (pid) => {
  const matches = pid.match(/(\.[a-zA-Z][0-9]{4}|\d{1}\w{1}\d{3})(\w{1,})/);
  return !!matches;
};

/**
 * Guess from an ID which product type is.
 * @param {string} productID ID of a product
 * @returns {string} estimated product type
 */
const getProductTypeOfID = (productID) => {
  let type = null;
  const FRONT_CONFIG = getFrontConfig();
  if (isVariationGroupId(productID)) {
    type = FRONT_CONFIG.productType.variationGroup;
  } else if (isVariationProductId(productID)) {
    type = FRONT_CONFIG.productType.variant;
  } else if (isMasterProductId(productID)) {
    type = FRONT_CONFIG.productType.master;
  }
  return type;
};

const getVariationGroupId = (pid) => {
  if (isMasterProductId(pid)) {
    throw new Error('Variation group id cannot be extracted from a master ID');
  }
  return getPidWithoutSize(pid);
};

export {
  getColorCodeFromPid,
  getMasterProductId,
  getPidWithoutSize,
  getProductTypeOfID,
  getVariationGroupId,
  addSizeToVariationGroupId,
  isMasterProductId,
  isVariationGroupId,
  isVariationProductId,
};
